(ns app.front.review-v2.subs
  (:require [app.domain.acceptance.model :as acceptance-model]
            [app.domain.application.shared-core :as app-shared]
            [app.domain.group.model :as group-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.review.model :as review-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.requirement.subs :as req-subs]
            [app.front.view.application :as-alias app-view]
            [app.front.view.navigation :as navigation]
            [app.front.view.party.subs-req :as party-req-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [core.db.entity.util :as eu]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.session :as session]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [goog.string :as gstring]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::review-data
  :-> ::review-data)

(rf/reg-sub ::selected-sub-review
  :<- [::review-data]
  (fn [data [_ id]]
    (get-in data [id :selected-sub-review])))

(rf/reg-sub ::review-comment
  :<- [::review-data]
  (fn [data [_ id]]
    (get-in data [id :review-comment])))

(defn acceptance-accepted? [acceptance]
  (-> acceptance :acceptance/value (= :acceptance.value/ok)))

(defn- review-sig [[_ review-id]]
  (rf/subscribe [::entities/entity review-id]))

(def edit-items-signal-candidates
  [:process.signal/edit-review-items
   :process.signal/edit-held-review-items])

(defn- available-review-results-handler [[{:review/keys [acceptances] :as review} request-changes-forbidden?] _]
  (let [latest-drafts (->> acceptances
                           (filter (du/in-state-fn :acceptance.state/draft))
                           (sort-by :entity/created)
                           (group-by (comp :db/id acceptance-model/acceptance-parent))
                           (map (comp last val)))
        can-approve?  (if (review-model/final-review? review)
                        (and (review-model/all-linked-reviews-approved? review)
                             (not-any? (acceptance-model/has-value-fn :acceptance.value/needs-changes)
                                       latest-drafts))
                        ;; only allow approved if we don't have new unaccepted acceptances created on this session
                        (or request-changes-forbidden?
                            (every? acceptance-accepted? latest-drafts)))]
    (->> [(when can-approve? :review.status/approved)
          (when-not request-changes-forbidden? :review.status/changes-requested)]
         (filter some?))))

(rf/reg-sub ::available-review-results
  (fn [[_ process-target review-id] _]
    [(rf/subscribe [::entities/entity review-id])
     (rf/subscribe [::process/signal-forbidden?
                    process-target
                    [:process.signal/request-changes-for-review (util/mmap review-id)]])])
  available-review-results-handler)

(rf/reg-sub ::review-result
  (fn [[_ process-target review-id]]
    [(rf/subscribe [::review-data])
     (rf/subscribe [::available-review-results process-target review-id])])
  (fn [[data available-results] [_ _ id]]
    ;; nil if previously selected result is no longer available
    (or ((set available-results) (get-in data [id :review-result]))
        (when (-> available-results count (= 1))
          (first available-results)))))

(rf/reg-sub ::review-time
  :<- [::review-data]
  (fn [data [_ id]]
    (get-in data [id :review-time])))

(rf/reg-sub ::editing-items?
  :<- [::review-data]
  (fn [data [_ id]]
    (get-in data [id :editing-items?])))

(rf/reg-sub ::forms-selected
  (fn [[_ review-id]]
    [(rf/subscribe [::review-data])
     (rf/subscribe [::saved-form-ids review-id])])
  (fn [[data saved-forms] [_ id]]
    (or (get-in data [id :forms-selected])
        saved-forms)))

(rf/reg-sub ::changes-for-selected-items?
  (fn [[_ review-id]]
    [(rf/subscribe [::saved-form-ids review-id])
     (rf/subscribe [::forms-selected review-id])
     (rf/subscribe [::saved-attachment-ids review-id])
     (rf/subscribe [::attachments-selected review-id])])
  (fn [[saved-forms selected-forms saved-attachments selected-attachments] _]
    (or (not= saved-forms selected-forms)
        (not= saved-attachments selected-attachments))))

(rf/reg-sub ::show-initial-add-items-button?
  (fn [[_ review-id]]
    [(rf/subscribe [::saved-form-ids review-id])
     (rf/subscribe [::saved-attachment-ids review-id])
     (rf/subscribe [::editing-items? review-id])])
  (fn [[saved-forms saved-attachments editing-items?] _]
    (and (not (or (seq saved-forms) (seq saved-attachments)))
         (not editing-items?))))

(rf/reg-sub ::attachments-selected
  (fn [[_ review-id]]
    [(rf/subscribe [::review-data])
     (rf/subscribe [::saved-attachment-ids review-id])])
  (fn [[data saved-attachments] [_ id]]
    (or (get-in data [id :attachments-selected])
        saved-attachments)))

(rf/reg-sub ::party-ids-selected
  (fn [[_ review-id]]
    [(rf/subscribe [::review-data])
     (rf/subscribe [::saved-party-ids review-id])])
  (fn [[data saved-parties] [_ id]]
    (or (get-in data [id :parties-selected])
        saved-parties)))

(rf/reg-event-db ::update-form-selection
  (fn [db [_ id new-set]]
    (assoc-in db [::review-data id :forms-selected] new-set)))

(rf/reg-event-db ::update-attachment-selection
  (fn [db [_ id new-set]]
    (assoc-in db [::review-data id :attachments-selected] new-set)))

(rf/reg-event-db ::update-party-selection
  (fn [db [_ id new-set]]
    (assoc-in db [::review-data id :parties-selected] new-set)))

(rf/reg-event-db ::review-result
  (fn [db [_ id review-result]]
    (assoc-in db [::review-data id :review-result] review-result)))

(rf/reg-event-db ::review-time
  (fn [db [_ id review-time]]
    (assoc-in db [::review-data id :review-time] review-time)))

(rf/reg-event-db ::set-review-comment
  (fn [db [_ id review-comment]]
    (assoc-in db [::review-data id :review-comment] review-comment)))

(rf/reg-event-db ::selected-sub-review
  (fn [db [_ id selected-sub-review]]
    (assoc-in db [::review-data id :selected-sub-review] selected-sub-review)))

(rf/reg-sub ::show-all-comments?
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :show-all-comments?] false)))

(rf/reg-event-db ::toggle-all-comments
  (fn [db [_ review-id]]
    (update-in db [::review-data review-id :show-all-comments?] not)))

(rf/reg-event-db ::set-description
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :description] value)))

(rf/reg-sub ::description
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :description])))

(rf/reg-event-db ::set-assignee
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :assignee] value)))

(rf/reg-sub ::assignee
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :assignee])))

(rf/reg-sub ::assignee-set?
  :<- [::review-data]
  (fn [data [_ review-id]]
    (contains? (get data review-id) :assignee)))

(rf/reg-event-db ::set-assignment-group
  (fn [db [_ review-id value]]
    (cond-> (assoc-in db [::review-data review-id :assignment-group] value)
      value (p/dissoc-in [::review-data review-id :assignee]))))

(rf/reg-sub ::assignment-group
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :assignment-group])))

(rf/reg-sub ::assignment-group-set?
  :<- [::review-data]
  (fn [data [_ review-id]]
    (contains? (get data review-id) :assignment-group)))

(rf/reg-event-db ::clear-review-data
  (fn [db [_ id]]
    (p/dissoc-in db [::review-data id])))

(rf/reg-sub ::route-review-id
  :<- [:route/match]
  :-> (comp :review-id :query :parameters))

(rf/reg-sub ::review-back-href
  :<- [::route-review-id]
  :<- [::ws-subs/workspace]
  (fn [[review-id workspace] _]
    (let [workspace-id (:workspace/id workspace)]
      (when (and review-id workspace-id)
        (href/href :view/workspace-application
                   {:workspace-id workspace-id}
                   {:review-id review-id})))))

(rf/reg-sub ::expanded-reviews
  (fn [db _]
    (::expanded-reviews db #{})))

(rf/reg-event-fx ::toggle-review-expansion
  (fn [{:keys [db]} [_ review-id]]
    (let [expanding? (not (contains? (::expanded-reviews db) review-id))]
      (cond-> {:db (update db ::expanded-reviews util/toggle-in-set review-id)}
        expanding? (assoc :dispatch [::clear-review-data review-id])))))

(def section-id :authority-review-section)

(defn review-row-id [review-id]
  (str "review-" review-id))

(rf/reg-event-db ::expand-review
  (fn [db [_ review-id]]
    (update db ::expanded-reviews (fnil conj #{}) review-id)))

(rf/reg-event-db ::collapse-reviews
  (fn [db _]
    (dissoc db ::expanded-reviews)))

(rf/reg-event-fx ::focus-review
  (fn [_ [_ review-id]]
    {:fx [[:dispatch [::clear-review-data review-id]]
          [:dispatch [::expand-review review-id]]
          [:dispatch [::navigation/scroll-to-id (review-row-id review-id)]]]}))

(rf/reg-event-fx ::begin-review
  (fn [_ [_ application review-id]]
    {:fx [[:dispatch [::expand-review review-id]]
          [:dispatch [::process/signal application [:process.signal/begin-and-create-sub-review {:review-id review-id}]]]]}))

(rf/reg-event-db ::toggle-editing-items
  (fn [db [_ id editing?]]
    (assoc-in db [::review-data id :editing-items?] editing?)))

(rf/reg-event-fx ::reassign-review
  (fn [{:keys [db]} [_ application review-id assignee-id]]
    {:db       (assoc-in db [::review-data review-id :assignee] assignee-id)
     :dispatch [::process/signal
                application
                [:process.signal/reassign-review
                 {:review-id       review-id
                  :review/assignee assignee-id}]
                {:on-done [::clear-review-data review-id]}]}))

(rf/reg-event-fx ::reassign-review-to-group
  (fn [{:keys [db]} [_ application review-id group-id]]
    {:db       (assoc-in db [::review-data review-id :assignment-group] group-id)
     :dispatch [::process/signal
                application
                [:process.signal/reassign-review
                 {:review-id                review-id
                  :review/assigned-to-group group-id}]
                {:on-done [::clear-review-data review-id]}]}))

(rf/reg-sub ::assignment-groups
  :<- [::entities/entities {:entity/type :entity.type/group
                            :group/type  :group.type/user-group}]
  (fn [groups [_ authority-group-id]]
    (->> groups
         (filter (partial review-model/is-assignment-group-of? authority-group-id))
         (sort-by :group/name))))

(rf/reg-sub ::review-configs
  (fn [[_ review-config-ids]]
    (rf/subscribe [::entities/entities review-config-ids]))
  (fn [review-configs]
    (sort-by :ordered/order review-configs)))

(rf/reg-event-fx ::send-review-signal
  (fn [_ [_ signal-kw application review-id sub-review-id minutes-spent review-comment]]
    {:dispatch [::process/signal
                application
                [signal-kw
                 (-> (util/mmap review-id)
                     (p/assoc-if :sub-review-id sub-review-id)
                     (p/assoc-if :sub-review/comment (not-empty review-comment))
                     (p/assoc-if :sub-review/minutes-spent minutes-spent))]
                {:on-success [::clear-review-data review-id]}]}))

(rf/reg-event-fx ::fetch-group-reviewers
  (fn [{:keys [db]} [_ group-entity-id :as event]]
    (if-some [user (:session/user db)]
      (when (or (contains? (->> user
                                :user/authority-groups
                                (map :db/id)
                                set)
                           group-entity-id)
                (session/admin? user))
        {:dispatch [:comm/command-monitored {:command    :group/review-users
                                             :data       {:authority-group group-entity-id}
                                             :on-success [::entities/merge-entity-db]}]})
      ;; User not yet loaded, wait for it
      {:dispatch [::session/wait-for-user-data event]})))

(rf/reg-sub ::group-reviewers
  :<- [:session/user]
  :<- [::entities/entities {:entity/type :entity.type/user}]
  (fn [[user users] [_ auth-group]]
    (->> users
         (filter (partial review-model/is-reviewer? auth-group))
         (sort (partial user-model/user-sort (:db/id user))))))

(rf/reg-event-db ::show-remove-modal
  (fn [db [_ review-id]]
    (assoc db ::review-to-remove review-id)))

(rf/reg-event-db ::remove-modal-cancel-clicked
  (fn [db _]
    (dissoc db ::review-to-remove)))

(rf/reg-sub ::review-to-remove
  :-> ::review-to-remove)

(rf/reg-sub ::show-remove-modal?
  :<- [::review-to-remove]
  :-> boolean)

(rf/reg-event-db ::show-edit-due-date-modal
  (fn [db [_ review-id current-due-date]]
    (assoc db ::edit-due-date {:review-id      review-id
                               :original-value current-due-date
                               :value          current-due-date})))

(rf/reg-event-db ::edit-due-date-modal-cancel-clicked
  (fn [db _]
    (dissoc db ::edit-due-date)))

(rf/reg-event-db ::set-edit-due-date
  (fn [db [_ value]]
    (assoc-in db [::edit-due-date :value] value)))

(rf/reg-sub ::edit-due-date
  :-> ::edit-due-date)

(rf/reg-sub ::edit-due-date-unchanged?
  (fn [[_ review-id]]
    [(rf/subscribe [::edit-due-date])
     (rf/subscribe [::config-authority-group review-id])])
  (fn [[{:keys [value original-value]} auth-group] _]
    (= (date-util/end-of-day-in-tz original-value (:group/tz auth-group))
       (date-util/end-of-day-in-tz value (:group/tz auth-group)))))

(rf/reg-sub ::show-edit-due-date-modal?
  :<- [::edit-due-date]
  :-> boolean)

(rf/reg-event-fx ::submit-edit-due-date
  (fn [{:keys [db]} [_ application]]
    (let [{:keys [review-id value]} (::edit-due-date db)]
      {:db (dissoc db ::edit-due-date)
       :fx [[:dispatch [::process/signal
                        application
                        ;; Omitting :review/due-date instructs the backend action to clear the due date.
                        [:process.signal/set-due-date
                         (p/assoc-if {:review-id review-id} :review/due-date value)]
                        {:on-success [::set-due-date review-id value]}]]]})))

(rf/reg-event-db ::show-start-new-round-modal
  (fn [db [_ review-id]]
    (assoc db ::start-new-round-review review-id)))

(rf/reg-event-db ::close-start-new-round-modal
  (fn [db _]
    (dissoc db ::start-new-round-review)))

(rf/reg-sub ::start-new-round-review
  :-> ::start-new-round-review)

;; Move-out confirmation modal - shown when dragging a held review after Final Review
(rf/reg-event-db ::show-move-out-from-final-review-modal
  (fn [db [_ {:keys [review-id from to application]}]]
    (assoc db ::move-out-from-final-review-pending {:review-id   review-id
                                                    :from        from
                                                    :to          to
                                                    :application application})))

(rf/reg-event-db ::move-out-from-final-review-modal-cancel
  (fn [db _]
    (dissoc db ::move-out-from-final-review-pending)))

(rf/reg-sub ::move-out-from-final-review-pending
  :-> ::move-out-from-final-review-pending)

(rf/reg-sub ::show-move-out-from-final-review-modal?
  :<- [::move-out-from-final-review-pending]
  :-> boolean)

(rf/reg-sub ::new-review
  :-> ::new-review)

(defn assign-default-reviewer [db key value]
  (if (= key :review/review-config)
    (let [entity-db        (::entities/entity-db db)
          review-config    (get entity-db value)
          default-assignee (:review.config/default-assignee review-config)
          assignee-entity  (when default-assignee (get entity-db default-assignee))]
      (cond
        (eu/is-type? :entity.type/user assignee-entity)
        (-> db
            (assoc-in [::new-review :review/assignee] default-assignee)
            (p/dissoc-in [::new-review :review/assigned-to-group]))

        (and (eu/is-type? :entity.type/group assignee-entity)
             (ff/enabled? :feature-flag.id/review-assignment-groups))
        (-> db
            (assoc-in [::new-review :review/assigned-to-group] default-assignee)
            (p/dissoc-in [::new-review :review/assignee]))

        :else
        (-> db
            (p/dissoc-in [::new-review :review/assignee])
            (p/dissoc-in [::new-review :review/assigned-to-group]))))
    db))

(defn- clear-counterpart-assignee [db key value]
  ;; A review can be assigned to an individual reviewer or an assignment group, never both.
  (cond
    (and (= key :review/assignee) (seq (str value)))
    (p/dissoc-in db [::new-review :review/assigned-to-group])

    (and (= key :review/assigned-to-group) (seq (str value)))
    (p/dissoc-in db [::new-review :review/assignee])

    :else db))

(rf/reg-event-db ::update-review-form
  (fn [db [_ key value]]
    (-> (if (= key :review/review-config)
          (p/dissoc-in db [::new-review :review/due-date])
          db)
        (assoc-in [::new-review key] value)
        (assign-default-reviewer key value)
        (clear-counterpart-assignee key value))))

(rf/reg-sub ::new-review-form-invalid?
  :<- [::new-review]
  (fn [{:review/keys [review-config]}]
    (nil? review-config)))

(rf/reg-event-db ::reset-form
  (fn [db _]
    (assoc db ::new-review {:review/description ""})))

(rf/reg-event-db ::remove-form
  (fn [db _]
    (dissoc db ::new-review ::assignees)))

(rf/reg-event-fx ::toggle-new-review-form
  (fn [{:keys [db]} _]
    {:dispatch (if (::new-review db)
                 [::remove-form]
                 [::reset-form])}))

(rf/reg-sub ::review-form-visible?
  :-> ::new-review)

(rf/reg-event-db ::new-review.success
  (fn [db]
    (dissoc db ::new-review)))

(rf/reg-event-fx ::new-review
  (fn [_ [_ application-id review-form-data]]
    {:dispatch [::process/signal
                application-id
                [:process.signal/create-review
                 (p/remove-vals nil? review-form-data)]
                {:notify     {:success :changes-saved}
                 :on-success [::new-review.success]}]}))

(rf/reg-sub ::latest-review-comment
  (fn [[_ review-id] _]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/sub-review
                                        :entity/parent review-id}]))
  (fn [sub-reviews]
    (some->> sub-reviews
             (sort-by :entity/created >)
             first
             :sub-review/comment)))

(rf/reg-sub ::time-tracking?
  :<- [::ws-subs/group-features]
  (fn [group-features]
    (-> group-features :review-time-tracking? boolean)))

(rf/reg-event-fx ::save-item-changes
  (fn [_ [_ signal-kw application review-id sub-review-id forms attachments]]
    {:dispatch [::process/signal
                application
                [signal-kw
                 (p/assoc-if {:review-id          review-id
                              :review/forms       forms
                              :review/attachments attachments}
                             :sub-review-id (when (= signal-kw :process.signal/edit-held-review-items)
                                              sub-review-id))]
                {:notify     {:success :changes-saved}
                 :on-success [::toggle-editing-items review-id false]}]}))

(rf/reg-event-db ::change-review-comment-content
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :review-comment-content] value)))

(rf/reg-event-db ::change-item-comment-content
  (fn [db [_ review-id item-id value]]
    (assoc-in db [::review-data review-id item-id :item-comment-content] value)))

(rf/reg-sub ::selected-review-comment-content
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :review-comment-content])))

(rf/reg-sub ::selected-item-comment-content
  :<- [::review-data]
  (fn [data [_ review-id item-id]]
    (get-in data [review-id item-id :item-comment-content])))

(rf/reg-event-fx ::set-new-item-comment
  (fn [{:keys [db]} [_ process-target review-id item-id draft-id value]]
    (if draft-id
      {:dispatch [::ee/save-debounce
                  process-target
                  :process.signal/edit-draft-acceptance
                  draft-id
                  :acceptance/text
                  value]}
      {:db (assoc-in db [::review-data review-id item-id :new-comment] value)})))

(rf/reg-sub ::new-item-comment
  :<- [::review-data]
  (fn [data [_ review-id item-id]]
    (get-in data [review-id item-id :new-comment])))

(rf/reg-event-fx ::add-cloudcheck-comment
  (fn [{:keys [db]} [_ application-id review-id item-id draft-id comment description analysis]]
    (let [current-comment (get-in db [::ee/unsaved draft-id :acceptance/text] comment)
          value           (str current-comment "<ul><li><p><strong>" (gstring/htmlEscape description)
                               "</strong></p><p>" (gstring/htmlEscape analysis) "</p></li></ul>")]
      {:db (-> db
               (ee/set-value draft-id :acceptance/text value)
               (assoc-in [::review-data review-id item-id :item-comment-content] :review/comments-for-this-review)
               (update-in [::review-data review-id item-id :comment-revision] (fnil inc 0)))
       :fx [[:dispatch [::ee/save application-id :process.signal/edit-draft-acceptance
                        draft-id :acceptance/text value]]]})))

(rf/reg-sub ::item-comment-revision
  :<- [::review-data]
  (fn [data [_ review-id item-id]]
    (get-in data [review-id item-id :comment-revision] 0)))

(rf/reg-event-fx ::set-review-item-acceptance-value
  (fn [{:keys [db]} [_ process-target review-id item-id draft-id value]]
    (if draft-id
      {:dispatch [::process/signal
                  process-target
                  [:process.signal/edit-draft-acceptance
                   (util/mmap {:id   draft-id
                               :attr :acceptance/value}
                              value)]
                  {:notify {:success :changes-saved}}]}
      ;; on-success dissoc unsaved from appdb
      {:db (assoc-in db [::review-data review-id item-id :item-acceptance-value] value)})))

(rf/reg-sub ::review-item-acceptance-value
  :<- [::review-data]
  (fn [data [_ review-id item-id]]
    (get-in data [review-id item-id :item-acceptance-value])))

(rf/reg-event-db ::set-review-item-visibility
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :item-visibility] value)))

(def item-visibility-options
  [:review/items-all :review/items-for-review])

(rf/reg-sub ::review-item-visibility
  :<- [::review-data]
  (fn [data [_ review-id]]
    (or (get-in data [review-id :item-visibility])
        (first item-visibility-options))))

(rf/reg-sub ::saved-form-ids
  review-sig
  (fn [review _]
    (->> review :review/forms du/entity-id-set)))

(rf/reg-sub ::saved-attachment-ids
  review-sig
  (fn [review _]
    (->> review :review/attachments du/entity-id-set)))

(rf/reg-sub ::saved-party-ids
  review-sig
  (fn [review _]
    (->> review :review/parties du/entity-id-set)))

(rf/reg-sub ::review-config
  review-sig
  (fn [review _]
    (->> review :review/review-config)))

(defn- unreviewed-ids-handler [[ctx saved-entity-ids] _]
  (->> (entities/find-entities ctx saved-entity-ids)
       (filter (comp (every-pred (du/in-state-fn :acceptance.state/draft)
                                 :acceptance/value)
                     acceptance-model/latest-acceptance))
       du/entity-id-set
       (set/difference saved-entity-ids)))

(rf/reg-sub ::unreviewed-form-ids
  (fn [[_ review-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::saved-form-ids review-id])])
  unreviewed-ids-handler)

(rf/reg-sub ::unreviewed-attachment-ids
  (fn [[_ review-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::saved-attachment-ids review-id])])
  unreviewed-ids-handler)

(defn- visible-entities-handler [[saved-entity-ids unreviewed-entity-ids item-visibility] _]
  (case item-visibility
    :review/items-all saved-entity-ids
    :review/items-for-review unreviewed-entity-ids))

(rf/reg-sub ::visible-attachment-ids
  (fn [[_ review-id]]
    [(rf/subscribe [::saved-attachment-ids review-id])
     (rf/subscribe [::unreviewed-attachment-ids review-id])
     (rf/subscribe [::review-item-visibility review-id])])
  visible-entities-handler)

(rf/reg-sub ::visible-form-ids
  (fn [[_ review-id]]
    [(rf/subscribe [::saved-form-ids review-id])
     (rf/subscribe [::unreviewed-form-ids review-id])
     (rf/subscribe [::review-item-visibility review-id])])
  visible-entities-handler)

(rf/reg-sub ::visible-party-ids
  (fn [[_ review-id]]
    [(rf/subscribe [::saved-party-ids review-id])
     (rf/subscribe [::review-item-visibility review-id])])
  (fn [[saved-entity-ids item-visibility] _]
    ;; No acceptances for parties
    (visible-entities-handler [saved-entity-ids #{} item-visibility] nil)))

(rf/reg-sub ::items-section-visible?
  (fn [[_ review-id]]
    (rf/subscribe [::editing-items? review-id]))
  :-> not)

(rf/reg-sub ::saved-review-forms
  (fn [[_ review-id] _]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::visible-form-ids review-id])])
  (fn [[ctx form-ids] _]
    (->> (entities/find-entities ctx form-ids)
         (sort-by #(-> % :form/schema :form.schema/name)))))

(rf/reg-sub ::saved-review-attachments
  (fn [[_ review-id] _]
    (rf/subscribe [::visible-attachment-ids review-id]))
  (fn [visible-attachment-ids _]
    (<sub [::entities/entities visible-attachment-ids])))

(rf/reg-sub ::saved-review-parties
  (fn [[_ review-id] _]
    (rf/subscribe [::visible-party-ids review-id]))
  (fn [visible-party-ids _]
    (<sub [::entities/entities visible-party-ids])))

(rf/reg-event-db ::set-request-attachment-modal-open
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :request-attachment-modal-open?] value)))

(rf/reg-sub ::request-attachment-modal-open?
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :request-attachment-modal-open?])))

(rf/reg-event-db ::select-attachment-type-for-request
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :attachment-type-for-request] value)))

(rf/reg-event-db ::clear-attachment-type-selection
  (fn [db [_ review-id]]
    (update-in db [::review-data review-id] dissoc :attachment-type-for-request)))

(rf/reg-sub ::attachment-type-for-request
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :attachment-type-for-request])))

(rf/reg-sub ::attachment-types
  (fn [[_ app-id] _]
    [(rf/subscribe [::app-view/attachment-types-with-i18n])
     (rf/subscribe [::entities/entities
                    {:entity/parent app-id
                     :entity/type   :entity.type/attachment}])])
  (fn [[attachment-types uploaded-attachments] _]
    (let [uploaded-att-ids (->> uploaded-attachments
                                (mapcat :attachment/types)
                                du/entity-id-set)]
      (->> attachment-types
           (keep (fn [[text id]]
                   (when (not (contains? uploaded-att-ids id))
                     {:value id :text text})))))))

(rf/reg-sub ::config-authority-group
  review-sig
  (fn [review _]
    (-> review :review/review-config :domain/authority-group)))

(rf/reg-sub ::due-date-default
  (fn [[_ review-id]]
    [(rf/subscribe [::review-config review-id])
     (rf/subscribe [::config-authority-group review-id])
     (rf/subscribe [::entities/entity review-id])])
  (fn [[review-config auth-group review] _]
    (review-model/calculate-due-date review
                                     review-config
                                     auth-group
                                     (js/Date.))))

(rf/reg-sub ::due-date
  (fn [[_ review-id]]
    [(rf/subscribe [::review-data])
     (rf/subscribe [::entities/entity review-id])
     (rf/subscribe [::due-date-default review-id])])
  (fn [[data review due-date-default] [_ review-id]]
    (if (= :review.status/draft (:review/status review))
      (get-in data [review-id :due-date] (or (:review/due-date review) due-date-default))
      (:review/due-date review))))

(rf/reg-event-db ::set-due-date
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :due-date] value)))

(rf/reg-event-db ::set-request-party-role-modal-open
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :request-party-role-modal-open?] value)))

(rf/reg-sub ::request-party-role-modal-open?
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :request-party-role-modal-open?])))

(rf/reg-event-db ::select-party-role-for-request
  (fn [db [_ review-id value]]
    (assoc-in db [::review-data review-id :party-role-for-request] value)))

(rf/reg-event-db ::clear-party-role-selection
  (fn [db [_ review-id]]
    (update-in db [::review-data review-id] dissoc :party-role-for-request)))

(rf/reg-sub ::party-role-for-request
  :<- [::review-data]
  (fn [data [_ review-id]]
    (get-in data [review-id :party-role-for-request])))

(rf/reg-sub ::unused-party-role-choices
  (fn [[_ phase-id]]
    [(rf/subscribe [::party-req-subs/unrequired-party-roles phase-id])
     (rf/subscribe [:language])])
  (fn [[party-roles lang] _]
    (->> party-roles
         (map (fn [role]
                {:value (:db/id role)
                 :text  (role-model/party-role-name role lang)}))
         (sort-by :text))))

(rf/reg-sub ::review-requirements
  review-sig
  (fn [review _]
    (->> review :review/requirements (map :db/id) set)))

(rf/reg-sub ::reviews
  (fn [[_ application-id]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/review
                                        :entity/parent application-id}]))
  (fn [reviews _]
    (->> reviews
         (sort-by :ordered/order))))

(defn visible-change-request? [review]
  (and (= :review.status/changes-requested (:review/status review))
       (not (review-model/held-review? review))))

(rf/reg-sub ::change-requests
  (fn [[_ phase-id]]
    (rf/subscribe [::reviews phase-id]))
  (fn [reviews _]
    (->> reviews
         (filter visible-change-request?))))

(rf/reg-sub ::has-change-requests?
  (fn [[_ phase-id]]
    (rf/subscribe [::change-requests phase-id]))
  (fn [change-requests _]
    (->> change-requests seq boolean)))

(rf/reg-sub ::sub-review+acceptances
  (fn [[_ phase-id]]
    (rf/subscribe [::change-requests phase-id]))
  (fn [reviews _]
    (->> reviews
         (map (fn [review]
                [;; review
                 review
                 ;; latest sub-review
                 (review-model/current-sub-review review)
                 ;; acceptances
                 (->> review
                      :review/acceptances
                      (group-by acceptance-model/acceptance-parent)
                      vals
                      (map (partial sort-by :entity/created))
                      (map last)
                      (remove #(-> % :acceptance/value (= :acceptance.value/ok)))
                      (remove nil?))])))))

(rf/reg-sub ::ws-review-configs
  :<- [::ws-subs/view-data]
  (fn [view-data _]
    (-> view-data :review-configs)))

(rf/reg-sub ::sub-reviews
  (fn [[_ review-id] _]
    (rf/subscribe [::entities/entities {:entity/parent review-id
                                        :entity/type   :entity.type/sub-review}]))
  (fn [sub-reviews _]
    (sort-by :entity/created > sub-reviews)))

(rf/reg-sub ::current-sub-review
  (fn [[_ review-id]]
    [(rf/subscribe [::sub-reviews review-id])])
  (fn [[sub-reviews] _]
    (first sub-reviews)))

(rf/reg-sub ::current-sub-review-id
  (fn [[_ review-id]]
    (rf/subscribe [::current-sub-review review-id]))
  :-> :db/id)

(rf/reg-sub ::published-sub-reviews
  (fn [[_ review-id]]
    (rf/subscribe [::sub-reviews review-id]))
  (fn [sub-reviews _]
    ;; Rounds without :domain/state predate the draft/published distinction and are always finished.
    (remove #(= :sub-review.state/draft (:domain/state %)) sub-reviews)))

;; Item level review comments given during a single review round, grouped by the type of the
;; commented item. When `active-only?` is true, comments of an unfinished round are left out.
(rf/reg-sub ::sub-review-acceptances
  (fn [[_ sub-review-id] _]
    (rf/subscribe [::entities/entity sub-review-id]))
  (fn [sub-review [_ _ active-only?]]
    (->> sub-review
         :sub-review/acceptances
         (filter #(or (not active-only?)
                      (du/in-state? :acceptance.state/active %)))
         (sort-by :entity/created)
         (group-by (comp :entity/type :entity/parent)))))

(rf/reg-sub ::reviewable-forms
  (fn [[_ application]]
    (rf/subscribe [::form-subs/application-forms application]))
  (fn [forms _]
    (->> forms
         (remove #(contains? (:entity/required-read-permissions %) :permission.form/authority-read)))))

(rf/reg-sub ::attachment-requirements
  (fn [[_ phase-id review-id]]
    [(rf/subscribe [::req-subs/all-attachment-requirements phase-id])
     (rf/subscribe [::review-requirements review-id])])
  (fn [[all-att-reqs review-req-ids] _]
    (->> all-att-reqs
         (filter #(contains? review-req-ids (:db/id %))))))

(rf/reg-sub ::party-role-requirements
  (fn [[_ phase-id review-id]]
    [(rf/subscribe [::party-req-subs/enriched-party-role-requirements phase-id])
     (rf/subscribe [::review-requirements review-id])])
  (fn [[all-party-reqs review-req-ids] _]
    (->> all-party-reqs
         (filter #(contains? review-req-ids (:db/id %))))))

(rf/reg-event-fx ::on-item-expand
  (fn [{:keys [db]} [_ {:keys [item-id review-id application-id]}]]
    (if (contains? (::queued-items db) item-id)
      {} ; Command already queued
      (let [context           (entities/db->context db)
            item              (entities/find-entity context item-id)
            attachment?       (eu/is-type? :entity.type/attachment item)
            acceptance-parent (if attachment?
                                (->> item
                                     :attachment/versions
                                     (remove :attachment.version/authority-markup?)
                                     (sort-by :attachment.version/version >)
                                     first)
                                item)
            target-id         (:db/id acceptance-parent)
            draft             (->> (entities/find-entities context {:entity/type   :entity.type/acceptance
                                                                    :domain/state  :acceptance.state/draft
                                                                    :entity/parent target-id})
                                   (filter #(-> % :review/_acceptances du/entity-id-set (contains? review-id)))
                                   eu/latest)]
        (if (some? draft)
          {} ; Draft already exists
          {:db       (update db ::queued-items (fnil conj #{}) item-id)
           :dispatch [::process/signal
                      application-id
                      [:process.signal/create-draft-acceptance
                       (util/mmap target-id review-id)]
                      {:on-done [::draft-created-done item-id]}]})))))

(rf/reg-event-fx ::draft-created-done
  (fn [{:keys [db]} [_ item-id]]
    {:db (update db ::queued-items disj item-id)}))

(rf/reg-sub ::plan-review-tool-name
  :<- [::ws-subs/authority-group]
  :<- [:session/user]
  :<- [:tr]
  (fn [[group user tr] _]
    (let [service              (-> group
                                   :group/plan-review-integration-config
                                   :integration-config/service)
          bluebeam-feature?    (group-model/has-feature? group :group.feature/enable-bluebeam)
          user-bluebeam?       (:user/bluebeam-integration? user)
          plan-review-feature? (group-model/has-feature? group :group.feature/external-plan-review-integration)
          bluebeam-service?    (= service :integration-config.service/bluebeam)
          dige-service?        (= service :integration-config.service/digeplan)]
      (cond
        (and bluebeam-feature? user-bluebeam? bluebeam-service?) (tr [service])
        (and plan-review-feature? dige-service?) (tr [service])
        :else nil))))

(rf/reg-sub ::applicable-errors
  (fn [[_ application-id signal]]
    [(rf/subscribe [::ff/enabled? :feature-flag.id/reviews-in-submitted])
     (rf/subscribe [::process/signal-errors-for-error-list application-id signal])])
  (fn [[reviews-in-submitted? errors] _]
    ;; some guards are not interested in the error list
    (let [ignored-guard-types (cond-> #{:process.guard/required-permissions
                                        :process.guard/review-in-status?}
                                (not reviews-in-submitted?)
                                (conj :process.guard/review-created-in-current-step?))]
      (->> errors
           (remove (comp ignored-guard-types first))))))

(rf/reg-sub ::applicable-errors-for-signals
  (fn [[_ application-id & signals]]
    (mapv #(rf/subscribe [::applicable-errors application-id %]) signals))
  (fn [errors _]
    (->> (apply concat errors)
         (remove (comp #{:process/illegal-signal} first))
         distinct)))

(rf/reg-sub ::integration-config-service
  :<- [::ws-subs/authority-group]
  (fn [group _]
    (-> group
        :group/plan-review-integration-config
        :integration-config/service)))

(rf/reg-sub ::integration-authentication-processing?
  :<- [::integration-config-service]
  (fn [service _]
    (case service
      :integration-config.service/bluebeam
      (<sub [:comm/command-processing? :user/set-bluebeam-authentication])
      false)))

(rf/reg-event-fx ::set-review-tab
  (fn [{:keys [db]} [_ tab]]
    (let [{:keys [data parameters]} (:route/match db)]
      (cond-> {:db (assoc db ::review-tab tab)}
        (-> parameters :query :review-id)
        (assoc :route/replace {:route  (:name data)
                               :params (:path parameters)
                               :query  (-> parameters :query (dissoc :review-id))})))))

(rf/reg-event-db ::reset-review-tab
  (fn [db _]
    (dissoc db ::review-tab)))

(rf/reg-sub ::review-tab
  :-> ::review-tab)

(rf/reg-sub ::active-review-tab
  (fn [[_ application-id]]
    [(rf/subscribe [::review-tab])
     (rf/subscribe [::route-review-id])
     (rf/subscribe [::reviews application-id])
     (rf/subscribe [::entities/entity application-id])])
  (fn [[manual-tab review-id reviews application] _]
    (let [focused (when review-id (p/find-first reviews {:db/id review-id}))]
      (or (when (and focused (not (app-shared/review-created-in-current-step? application focused)))
            :all-reviews)
          manual-tab
          :current-step))))

(rf/reg-sub ::review-editable?
  (fn [[_ application-id review-id] _]
    [(rf/subscribe [::entities/entity application-id])
     (rf/subscribe [::entities/entity review-id])
     (rf/subscribe [::active-review-tab application-id])
     (rf/subscribe [::edit-items-signal application-id review-id])])
  (fn [[application review active-tab edit-items-signal] _]
    (and (app-shared/review-created-in-current-step? application review)
         (not= :all-reviews active-tab)
         edit-items-signal)))

(rf/reg-sub ::edit-items-signal
  (fn [[_ application-id review-id] _]
    (mapv #(rf/subscribe [::process/signal-permitted?
                          application-id
                          [% (util/mmap review-id)]])
          edit-items-signal-candidates))
  (fn [permitted? _]
    (-> (map vector edit-items-signal-candidates permitted?)
        (p/find-first #(true? (second %)))
        first)))

(rf/reg-sub ::reviews-linked-to-final
  (fn [[_ final-review-id] _]
    (rf/subscribe [::entities/entity final-review-id]))
  :-> review-model/reviews-linked-to-final)

(rf/reg-sub ::final-review-completion-stats
  (fn [[_ final-review-id] _]
    (rf/subscribe [::reviews-linked-to-final final-review-id]))
  (fn [linked-reviews _]
    (let [total     (count linked-reviews)
          completed (->> linked-reviews (filter :review/end) count)]
      {:total     total
       :completed completed})))

(rf/reg-sub ::final-review-ready?
  (fn [[_ final-review-id] _]
    (rf/subscribe [::entities/entity final-review-id]))
  :-> review-model/final-review-ready?)

(rf/reg-sub ::awaiting-final-review?
  review-sig
  :-> review-model/linked-to-incomplete-final-review?)

(rf/reg-sub ::held-reviews
  (fn [[_ final-review-id] _]
    (rf/subscribe [::entities/entity final-review-id]))
  :-> review-model/held-reviews)
